import { useState, useEffect, useMemo } from 'react'
import {
  X, Mail, Calendar, Package, CreditCard, Shield, Gift, Clock,
  CheckCircle, XCircle, Power, Wallet, Wifi, WifiOff,
  MapPin, User, DollarSign, FileText, Trash2, Plus, Receipt, Loader,
  Download, RefreshCw
} from 'lucide-react'
import SubscriptionService from '../services/SubscriptionService'
import ClientService from '../services/ClientService'
import './ClientDetail.css'

const PLANS = {
  free:      { name: 'Sin plan', color: '#6b7280', icon: '🆓' },
  basico:    { name: 'Básico',   color: '#3b82f6', icon: '⚡' },
  pro:       { name: 'Pro',      color: '#8b5cf6', icon: '🚀' },
  comercial: { name: 'Comercial', color: '#f59e0b', icon: '🏢' },
}

const fmtReportSize = (b) => {
  if (b === null || b === undefined) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

export default function ClientDetail({
  client,
  onClose,
  warranties = [],
  notes = [],
  tickets = [],
  billing = {},
  onToggleSubscription,
  onToggleTrial,
  onToggleWhitelist,
  onManualPayment,
  onChangePlan,
  onAddNote,
  onDeleteNote,
  formatDate,
}) {
  const [activeSection, setActiveSection] = useState('overview')
  const [showPlanDropdown, setShowPlanDropdown] = useState(false)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState(null)

  // Reportes de telemetria
  const [reports, setReports] = useState([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [reportsError, setReportsError] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [generateMsg, setGenerateMsg] = useState(null)

  const loadReports = () => ClientService.listReports(client.accountId)
    .then((data) => setReports(data.reports || []))
    .catch(() => {})

  useEffect(() => {
    if (activeSection !== 'reports') return
    let cancelled = false
    setReportsLoading(true)
    setReportsError(null)
    ClientService.listReports(client.accountId)
      .then((data) => { if (!cancelled) setReports(data.reports || []) })
      .catch((err) => { if (!cancelled) setReportsError(err.message || 'Error al cargar reportes') })
      .finally(() => { if (!cancelled) setReportsLoading(false) })
    return () => { cancelled = true }
  }, [activeSection, client.accountId])

  const handleGenerateReports = async (sendEmail) => {
    setGenerating(true)
    setGenerateMsg(null)
    try {
      const res = await ClientService.generateReports(client.accountId, { sendEmail })
      setGenerateMsg(`Generación disparada para ${res.rooms} sala(s)${sendEmail ? ' (con email al cliente)' : ''}. Aparecen en unos minutos.`)
      setTimeout(loadReports, 8000)
    } catch (err) {
      setGenerateMsg(err.message || 'Error al generar')
    } finally {
      setGenerating(false)
    }
  }

  const plan = PLANS[client.plan] || PLANS.free

  // Fetch payment history when subscription tab is selected
  useEffect(() => {
    if (activeSection !== 'subscription') return
    if (!client.mercadopagoPreapprovalId) return
    let cancelled = false
    setPaymentsLoading(true)
    setPaymentsError(null)
    SubscriptionService.getPaymentHistory(client.accountId)
      .then(data => {
        if (!cancelled) setPaymentHistory(data.payments || [])
      })
      .catch(err => {
        if (!cancelled) setPaymentsError(err.message || 'Error al cargar pagos')
      })
      .finally(() => { if (!cancelled) setPaymentsLoading(false) })
    return () => { cancelled = true }
  }, [activeSection, client.accountId, client.mercadopagoPreapprovalId])

  const clientWarranties = useMemo(() =>
    warranties.filter(w => w.clientId === client.accountId),
    [warranties, client.accountId]
  )

  const clientNotes = useMemo(() =>
    notes.filter(n => n.clientId === client.accountId),
    [notes, client.accountId]
  )

  const clientTickets = useMemo(() =>
    tickets.filter(t => t.clientId === client.accountId || (t.clientEmail && t.clientEmail === client.email)),
    [tickets, client.accountId, client.email]
  )

  const devModels = useMemo(() => {
    return (client.devices || []).reduce((acc, d) => {
      const m = d.model || 'Sin modelo'
      acc[m] = (acc[m] || 0) + 1
      return acc
    }, {})
  }, [client.devices])

  // Status info
  const statusInfo = client.isWhitelisted
    ? { label: 'Whitelist', color: '#14b8a6', icon: Shield, bg: 'rgba(20,184,166,0.12)' }
    : client.isTrial
    ? { label: client.trialExpired ? 'Trial Expirado' : `Trial (${client.trialDaysRemaining ?? '?'}d)`, color: '#8b5cf6', icon: Gift, bg: 'rgba(139,92,246,0.12)' }
    : client.subscriptionActive
    ? { label: 'Activa', color: '#10b981', icon: CheckCircle, bg: 'rgba(16,185,129,0.12)' }
    : client.isExpired
    ? { label: 'Vencida', color: '#f59e0b', icon: Clock, bg: 'rgba(245,158,11,0.12)' }
    : { label: 'Bloqueada', color: '#ef4444', icon: XCircle, bg: 'rgba(239,68,68,0.12)' }

  const StatusIcon = statusInfo.icon

  const sections = [
    { id: 'overview', label: 'General' },
    { id: 'devices', label: `Dispositivos (${client.deviceCount || 0})` },
    { id: 'subscription', label: 'Suscripción' },
    { id: 'reports', label: 'Reportes' },
    { id: 'warranties', label: `Garantías (${clientWarranties.length})` },
    { id: 'activity', label: 'Actividad' },
  ]

  return (
    <div className="cd-overlay" onClick={onClose}>
      <div className="cd-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cd-header">
          <div className="cd-header-left">
            <div className="cd-avatar" style={{ background: `${plan.color}20`, color: plan.color }}>
              {(client.name || 'U')[0].toUpperCase()}
            </div>
            <div className="cd-header-info">
              <h2 className="cd-name">{client.name || 'Sin nombre'}</h2>
              <div className="cd-email"><Mail size={13} /> {client.email || 'Sin email'}</div>
            </div>
          </div>
          <div className="cd-header-right">
            <span className="cd-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
              <StatusIcon size={13} /> {statusInfo.label}
            </span>
            <button className="cd-close" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="cd-sections">
          {sections.map(s => (
            <button
              key={s.id}
              className={`cd-section-tab ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="cd-body">

          {/* ── General ── */}
          {activeSection === 'overview' && (
            <div className="cd-section">
              <div className="cd-info-grid">
                <div className="cd-info-card">
                  <div className="cd-info-icon"><User size={16} /></div>
                  <div>
                    <div className="cd-info-label">Account ID</div>
                    <div className="cd-info-value mono">{client.accountId}</div>
                  </div>
                </div>
                <div className="cd-info-card">
                  <div className="cd-info-icon"><Calendar size={16} /></div>
                  <div>
                    <div className="cd-info-label">Registro</div>
                    <div className="cd-info-value">{formatDate(client.registrationDate)}</div>
                  </div>
                </div>
                <div className="cd-info-card">
                  <div className="cd-info-icon"><Package size={16} /></div>
                  <div>
                    <div className="cd-info-label">Dispositivos</div>
                    <div className="cd-info-value">
                      {client.deviceCount || 0} total
                      {client.hasActiveDevices && <span className="cd-green"> · {client.activeDeviceCount} online</span>}
                    </div>
                  </div>
                </div>
                <div className="cd-info-card">
                  <div className="cd-info-icon" style={{ color: plan.color }}><CreditCard size={16} /></div>
                  <div>
                    <div className="cd-info-label">Plan</div>
                    <div className="cd-info-value" style={{ color: plan.color }}>{plan.icon} {plan.name}</div>
                  </div>
                </div>
                {client.subscriptionExpiresAt && !client.isWhitelisted && (
                  <div className="cd-info-card">
                    <div className="cd-info-icon"><Clock size={16} /></div>
                    <div>
                      <div className="cd-info-label">Vencimiento</div>
                      <div className="cd-info-value">{formatDate(client.subscriptionExpiresAt)}</div>
                    </div>
                  </div>
                )}
                {billing.total > 0 && (
                  <div className="cd-info-card">
                    <div className="cd-info-icon" style={{ color: '#10b981' }}><DollarSign size={16} /></div>
                    <div>
                      <div className="cd-info-label">Revenue total</div>
                      <div className="cd-info-value cd-green">${billing.total.toLocaleString('es-AR')}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rooms */}
              {client.growRooms?.length > 0 && (
                <div className="cd-subsection">
                  <h4 className="cd-subsection-title"><MapPin size={14} /> Grows</h4>
                  <div className="cd-tags">
                    {client.growRooms.map((room, i) => (
                      <span key={i} className="cd-room-tag">🌱 {room.name}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Device model summary */}
              {Object.keys(devModels).length > 0 && (
                <div className="cd-subsection">
                  <h4 className="cd-subsection-title"><Package size={14} /> Modelos</h4>
                  <div className="cd-tags">
                    {Object.entries(devModels).map(([model, count]) => (
                      <span key={model} className="cd-model-tag">{count}× {model}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Devices ── */}
          {activeSection === 'devices' && (
            <div className="cd-section">
              {(client.devices || []).length === 0 ? (
                <div className="cd-empty"><Package size={28} /><span>Sin dispositivos registrados</span></div>
              ) : (
                <div className="cd-devices-list">
                  {(client.devices || []).map((dev, i) => (
                    <div key={dev.mac || i} className={`cd-device-card ${dev.isActive ? 'online' : ''}`}>
                      <div className="cd-device-status">
                        {dev.isActive ? <Wifi size={16} /> : <WifiOff size={16} />}
                      </div>
                      <div className="cd-device-info">
                        <div className="cd-device-mac">{dev.mac}</div>
                        <div className="cd-device-meta">
                          <span className="cd-device-model">{dev.model || 'Sin modelo'}</span>
                          {dev.name && <span className="cd-device-name">{dev.name}</span>}
                        </div>
                      </div>
                      <div className="cd-device-indicator">
                        <span className={`cd-online-dot ${dev.isActive ? 'on' : 'off'}`} />
                        {dev.isActive ? 'Online' : 'Offline'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Subscription ── */}
          {activeSection === 'subscription' && (
            <div className="cd-section">
              {/* Current status card */}
              <div className="cd-sub-status" style={{ borderLeftColor: statusInfo.color }}>
                <div className="cd-sub-status-header">
                  <span className="cd-sub-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                    <StatusIcon size={14} /> {statusInfo.label}
                  </span>
                  {client.manualPayment && <span className="cd-sub-manual-badge">💳 Pago manual</span>}
                  {client.billingCycle && client.billingCycle !== 'monthly' && (
                    <span className="cd-sub-cycle-badge">📆 Anual</span>
                  )}
                </div>
                <div className="cd-sub-plan-row">
                  <span className="cd-sub-plan" style={{ color: plan.color }}>{plan.icon} {plan.name}</span>
                  {client.subscriptionExpiresAt && !client.isWhitelisted && (
                    <span className="cd-sub-expires">
                      Vence: {formatDate(client.subscriptionExpiresAt)}
                    </span>
                  )}
                </div>
                {client.mercadopagoStatus && (
                  <div className="cd-sub-mp">
                    MercadoPago: <strong>{client.mercadopagoStatus}</strong>
                  </div>
                )}
                {client.lastPaymentDate && (
                  <div className="cd-sub-mp">Último pago: {formatDate(client.lastPaymentDate)}</div>
                )}
              </div>

              {/* Actions */}
              <div className="cd-sub-actions">
                <h4 className="cd-subsection-title">Acciones</h4>
                <div className="cd-actions-grid">
                  <button className={`cd-action ${client.subscriptionActive ? 'danger' : 'success'}`}
                    onClick={() => onToggleSubscription(client)}>
                    <Power size={14} /> {client.subscriptionActive ? 'Desactivar' : 'Activar'}
                  </button>
                  <button className={`cd-action ${client.isTrial ? 'muted' : 'trial'}`}
                    onClick={() => onToggleTrial(client)}>
                    <Gift size={14} /> {client.isTrial ? 'Quitar trial' : 'Dar trial'}
                  </button>
                  <button className={`cd-action ${client.isWhitelisted ? 'whitelisted' : 'whitelist'}`}
                    onClick={() => onToggleWhitelist(client)}>
                    <Shield size={14} /> {client.isWhitelisted ? 'Quitar Whitelist' : 'Agregar Whitelist'}
                  </button>
                  <button className="cd-action manual" onClick={() => onManualPayment(client)}>
                    <Wallet size={14} /> Cuota manual
                  </button>
                </div>

                {/* Plan selector */}
                <div className="cd-plan-select">
                  <h4 className="cd-subsection-title">Cambiar plan</h4>
                  <div className="cd-plan-options">
                    {Object.entries(PLANS).map(([key, p]) => (
                      <button
                        key={key}
                        className={`cd-plan-btn ${client.plan === key ? 'current' : ''}`}
                        style={{ '--plan-color': p.color }}
                        onClick={() => { if (client.plan !== key) onChangePlan(client, key) }}
                        disabled={client.plan === key}
                      >
                        <span className="cd-plan-icon">{p.icon}</span>
                        <span className="cd-plan-name">{p.name}</span>
                        {client.plan === key && <CheckCircle size={12} />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div className="cd-payment-history">
                <h4 className="cd-subsection-title"><Receipt size={14} /> Historial de pagos</h4>
                {!client.mercadopagoPreapprovalId ? (
                  <div className="cd-payment-empty">
                    Sin suscripción de MercadoPago vinculada
                  </div>
                ) : paymentsLoading ? (
                  <div className="cd-payment-loading"><Loader size={18} className="cd-spinner" /> Cargando pagos...</div>
                ) : paymentsError ? (
                  <div className="cd-payment-error">{paymentsError}</div>
                ) : paymentHistory.length === 0 ? (
                  <div className="cd-payment-empty">Sin pagos registrados</div>
                ) : (
                  <div className="cd-payment-list">
                    {paymentHistory.map((p, i) => {
                      const isApproved = p.status === 'approved'
                      const isRejected = p.status === 'rejected'
                      const isPending = p.status === 'in_process' || p.status === 'pending'
                      return (
                        <div key={p.id || i} className={`cd-payment-item ${isApproved ? 'approved' : isRejected ? 'rejected' : isPending ? 'pending' : ''}`}>
                          <div className="cd-payment-status-dot" />
                          <div className="cd-payment-info">
                            <div className="cd-payment-top">
                              <span className="cd-payment-amount">
                                ${(p.amount || 0).toLocaleString('es-AR')}
                              </span>
                              <span className={`cd-payment-status-label ${isApproved ? 'approved' : isRejected ? 'rejected' : 'pending'}`}>
                                {isApproved ? 'Aprobado' : isRejected ? 'Rechazado' : isPending ? 'Pendiente' : p.status}
                              </span>
                            </div>
                            <div className="cd-payment-meta">
                              <span>{formatDate(p.dateCreated)}</span>
                              {p.paymentMethodId && <span>{p.paymentMethodId}</span>}
                              {p.retryAttempt > 0 && <span>Intento #{p.retryAttempt}</span>}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Reportes de telemetria ── */}
          {activeSection === 'reports' && (
            <div className="cd-section">
              <div className="cd-rep-actions">
                <button className="cd-rep-btn" disabled={generating} onClick={() => handleGenerateReports(false)}>
                  {generating ? <Loader size={14} className="cd-spin" /> : <RefreshCw size={14} />}
                  Generar ahora
                </button>
                <button className="cd-rep-btn cd-rep-btn--ghost" disabled={generating} onClick={() => handleGenerateReports(true)}>
                  <Mail size={14} /> Generar + email al cliente
                </button>
              </div>
              {generateMsg && <div className="cd-rep-msg">{generateMsg}</div>}

              {reportsLoading && <div className="cd-empty"><Loader size={24} className="cd-spin" /><span>Cargando…</span></div>}
              {!reportsLoading && reportsError && <div className="cd-empty cd-error"><span>{reportsError}</span></div>}
              {!reportsLoading && !reportsError && reports.length === 0 && (
                <div className="cd-empty"><FileText size={28} /><span>Sin reportes todavía</span></div>
              )}
              {!reportsLoading && reports.length > 0 && (
                <table className="cd-rep-table">
                  <thead>
                    <tr><th>Archivo</th><th>Período</th><th>Tamaño</th><th></th></tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.key}>
                        <td className="cd-rep-name">{r.filename}</td>
                        <td>{r.period}</td>
                        <td>{fmtReportSize(r.size)}</td>
                        <td>
                          <a className="cd-rep-dl" href={r.url} target="_blank" rel="noopener noreferrer" download title="Descargar">
                            <Download size={15} />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Warranties ── */}
          {activeSection === 'warranties' && (
            <div className="cd-section">
              {clientWarranties.length === 0 ? (
                <div className="cd-empty"><Shield size={28} /><span>Sin garantías registradas</span></div>
              ) : (
                <div className="cd-warranties-list">
                  {clientWarranties.map(w => {
                    const isExpired = w.expiresAt && new Date(w.expiresAt) < new Date()
                    const rmas = JSON.parse(w.rmaHistory || '[]')
                    return (
                      <div key={w.$id} className={`cd-warranty-card ${isExpired ? 'expired' : ''}`}>
                        <div className="cd-warranty-header">
                          <span className="cd-warranty-model">{w.deviceModel}</span>
                          <span className={`cd-warranty-status ${isExpired ? 'expired' : 'active'}`}>
                            {isExpired ? 'Vencida' : 'Activa'}
                          </span>
                        </div>
                        <div className="cd-warranty-mac">{w.deviceMac}</div>
                        <div className="cd-warranty-dates">
                          <span>Compra: {formatDate(w.purchaseDate)}</span>
                          <span>Vence: {formatDate(w.expiresAt)}</span>
                        </div>
                        {rmas.length > 0 && (
                          <div className="cd-warranty-rmas">
                            {rmas.length} RMA(s)
                          </div>
                        )}
                        {w._auto && <span className="cd-auto-badge">Auto</span>}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Activity ── */}
          {activeSection === 'activity' && (
            <div className="cd-section">
              <div className="cd-activity-header">
                <h4 className="cd-subsection-title">Timeline</h4>
                <button className="cd-add-note-btn" onClick={() => onAddNote(client)}>
                  <Plus size={14} /> Nueva nota
                </button>
              </div>
              <div className="cd-timeline">
                {(() => {
                  const events = []
                  if (client.registrationDate) {
                    events.push({ id: 'reg', dot: '#3b82f6', icon: '🎉', label: 'Registro de cuenta', date: client.registrationDate })
                  }
                  ;(client.devices || []).forEach((d, i) => {
                    events.push({ id: `dev-${i}`, dot: '#10b981', icon: '📦', label: `Dispositivo: ${d.model || 'Sin modelo'} (${d.mac})`, date: d.createdAt })
                  })
                  clientWarranties.forEach(w => {
                    events.push({ id: `war-${w.$id}`, dot: '#8b5cf6', icon: '🛡️', label: `Garantía: ${w.deviceModel} (${w.deviceMac})`, date: w.purchaseDate })
                  })
                  const statusLabels = { open: 'Abierto', in_progress: 'En Progreso', resolved: 'Resuelto', closed: 'Cerrado' }
                  const priorityIcons = { high: '🔴', medium: '🟡', low: '🟢' }
                  clientTickets.forEach(t => {
                    events.push({
                      id: `tk-${t.$id}`, dot: t.status === 'open' ? '#ef4444' : '#6b7280',
                      icon: '🎫', label: `${priorityIcons[t.priority] || '🟡'} ${t.ticketNumber}: ${t.subject}`,
                      sublabel: `${statusLabels[t.status] || t.status}`, date: t.createdAt || t.$createdAt
                    })
                  })
                  const noteIcons = { note: '📝', call: '📞', email: '📧', meeting: '🤝', issue: '⚠️' }
                  clientNotes.forEach(n => {
                    events.push({ id: n.$id, dot: '#f59e0b', icon: noteIcons[n.type] || '📝', label: n.content, date: n.createdAt, deletable: true })
                  })
                  events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))

                  if (events.length === 0) {
                    return <div className="cd-empty"><FileText size={28} /><span>Sin actividad registrada</span></div>
                  }

                  return events.map(ev => (
                    <div key={ev.id} className="cd-event">
                      <div className="cd-event-dot" style={{ background: ev.dot }} />
                      <div className="cd-event-content">
                        <div className="cd-event-label">{ev.icon} {ev.label}</div>
                        {ev.sublabel && <div className="cd-event-sublabel">{ev.sublabel}</div>}
                        <div className="cd-event-date">{formatDate(ev.date)}</div>
                      </div>
                      {ev.deletable && (
                        <button className="cd-event-delete" onClick={() => onDeleteNote(ev.id)}><Trash2 size={12} /></button>
                      )}
                    </div>
                  ))
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
